// Jun-2026 — Project Opening Stock (previous-ERP carry-in). Bulk manual
// entry of Ops-Project stock WITHOUT a GRN. Items validate against the Item
// Master; created batches behave like normal locked project stock (PMC /
// transfers). Permission module: project_opening_stock.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SearchableSelect } from "@/components/SearchableSelect";
import { X, Plus, Trash2, PackagePlus } from "lucide-react";

const inputCls = "border border-slate-300 rounded px-2 py-1.5 text-xs w-full focus:border-emerald-500 focus:outline-none";
const blankLine = () => ({ item_id: "", qty: "", unit_rate: "", storage_location: "", remarks: "" });

export const ProjectOpeningStockDialog = ({ project, onClose, onSaved }) => {
  const { hasPerm } = useAuth();
  const canDelete = hasPerm("project_opening_stock", "delete");
  const [items, setItems] = useState([]);
  const [lines, setLines] = useState([blankLine()]);
  const [asOf, setAsOf] = useState("");
  const [remarks, setRemarks] = useState("Opening stock carried in from previous ERP");
  const [existing, setExisting] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadExisting = () =>
    api.get(`/projects/${project.id}/opening-stock`)
      .then((r) => setExisting(Array.isArray(r.data) ? r.data : []))
      .catch(() => setExisting([]));

  useEffect(() => {
    api.get("/items/_pickable")
      .then((r) => setItems((r.data || []).filter((i) => (i.item_type || "").toLowerCase() !== "service")))
      .catch(() => toast.error("Could not load the Item Master"));
    loadExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const setLine = (idx, patch) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const submit = async () => {
    const clean = lines.filter((l) => l.item_id || l.qty);
    if (!clean.length) { toast.error("Add at least one line"); return; }
    for (let i = 0; i < clean.length; i++) {
      if (!clean[i].item_id) { toast.error(`Line ${i + 1}: pick an item from the Item Master`); return; }
      if (!(Number(clean[i].qty) > 0)) { toast.error(`Line ${i + 1}: quantity must be greater than 0`); return; }
      if (Number(clean[i].unit_rate) < 0) { toast.error(`Line ${i + 1}: rate cannot be negative`); return; }
    }
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/opening-stock`, {
        as_of_date: asOf || "",
        remarks,
        lines: clean.map((l) => ({
          item_id: l.item_id,
          qty: Number(l.qty),
          unit_rate: Number(l.unit_rate) || 0,
          storage_location: l.storage_location || "",
          remarks: l.remarks || "",
        })),
      });
      toast.success(`${r.data.created} opening-stock batch(es) created — available for consumption`);
      setLines([blankLine()]);
      loadExisting();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create opening stock");
    } finally { setBusy(false); }
  };

  const del = async (b) => {
    if (!window.confirm(`Remove opening batch ${b.batch_code}? Only fully-unconsumed batches can be removed.`)) return;
    try {
      await api.delete(`/projects/${project.id}/opening-stock/${b.id}`);
      toast.success(`${b.batch_code} removed`);
      loadExisting();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-8 px-4"
      data-testid="opening-stock-dialog" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl shadow-2xl border border-emerald-300"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-emerald-200 bg-emerald-50 flex items-center gap-2">
          <PackagePlus size={15} className="text-emerald-700" />
          <div>
            <div className="text-sm font-black text-slate-800">Add Opening Stock (Bulk, no GRN) — {project.project_code}</div>
            <div className="text-[10px] text-slate-500">Open stocks from the previous ERP · validated against the Item Master · consumable like normal project stock</div>
          </div>
          <button onClick={onClose} data-testid="opening-stock-close" className="ml-auto text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>

        <div className="p-4 space-y-3 text-xs">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">As-of Date</div>
              <input type="date" data-testid="opening-stock-asof" className={inputCls} style={{ width: 150 }}
                value={asOf} onChange={(e) => setAsOf(e.target.value)} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Common Remarks</div>
              <input data-testid="opening-stock-remarks" className={inputCls} value={remarks}
                onChange={(e) => setRemarks(e.target.value)} placeholder="e.g. Tally stock as on 31-Mar-2026" />
            </div>
          </div>

          <div className="overflow-x-auto border border-slate-200">
            <table className="w-full min-w-[760px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["#", "Item (Item Master)", "Qty", "Unit Rate (₹)", "Storage Location", "Line Remarks", ""].map((h) => (
                    <th key={h} className="py-1.5 px-2 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b border-slate-100 align-top">
                    <td className="py-1.5 px-2 text-slate-400">{i + 1}</td>
                    <td className="py-1.5 px-2 min-w-[260px]">
                      <SearchableSelect
                        testid={`opening-stock-item-${i}`}
                        value={l.item_id}
                        items={items}
                        onChange={(id) => setLine(i, { item_id: id || "" })}
                        getKey={(it) => it.id}
                        getLabel={(it) => `${it.item_code || "?"} · ${it.name || "(unnamed)"}${it.unit ? ` (${it.unit})` : ""}`}
                        getSearchText={(it) => `${it.item_code || ""} ${it.name || ""}`.toLowerCase()}
                        placeholder="Search item…"
                        searchPlaceholder="Type item code or name…"
                        inputClassName={inputCls}
                      />
                    </td>
                    <td className="py-1.5 px-2 w-24">
                      <input type="number" min="0" step="0.001" data-testid={`opening-stock-qty-${i}`}
                        className={inputCls} value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} />
                    </td>
                    <td className="py-1.5 px-2 w-28">
                      <input type="number" min="0" step="0.01" data-testid={`opening-stock-rate-${i}`}
                        className={inputCls} value={l.unit_rate} onChange={(e) => setLine(i, { unit_rate: e.target.value })} />
                    </td>
                    <td className="py-1.5 px-2 w-32">
                      <input className={inputCls} value={l.storage_location}
                        onChange={(e) => setLine(i, { storage_location: e.target.value })} placeholder="optional" />
                    </td>
                    <td className="py-1.5 px-2">
                      <input className={inputCls} value={l.remarks}
                        onChange={(e) => setLine(i, { remarks: e.target.value })} placeholder="optional" />
                    </td>
                    <td className="py-1.5 px-2">
                      {lines.length > 1 && (
                        <button onClick={() => setLines((p) => p.filter((_, x) => x !== i))}
                          className="text-rose-500 hover:text-rose-700"><Trash2 size={13} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button data-testid="opening-stock-add-line" onClick={() => setLines((p) => [...p, blankLine()])}
            className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:underline">
            <Plus size={12} /> Add Line
          </button>

          {existing.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                Existing Opening Batches ({existing.length})
              </div>
              <div className="border border-slate-200 divide-y divide-slate-100 max-h-44 overflow-y-auto" data-testid="opening-stock-existing">
                {existing.map((b) => (
                  <div key={b.id} className="px-2 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
                    <span className="font-mono text-slate-700">{b.batch_code}</span>
                    <span className="text-slate-600">{b.item_code} · {b.item_name}</span>
                    <span className="tabular-nums">Qty {b.remaining_qty}/{b.received_qty} {b.unit}</span>
                    <span className="tabular-nums text-slate-500">@ ₹{Number(b.unit_rate || 0).toLocaleString("en-IN")}</span>
                    {b.consumed_qty > 0 && <span className="text-amber-700 text-[10px]">({b.consumed_qty} consumed)</span>}
                    {canDelete && b.consumed_qty === 0 && (
                      <button data-testid={`opening-stock-del-${b.batch_code}`} onClick={() => del(b)}
                        className="ml-auto text-rose-600 hover:text-rose-800"><Trash2 size={12} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
          <span className="text-[10px] text-slate-500">No GRN, no vendor, no GL — stock only. Deletable until first consumption.</span>
          <button data-testid="opening-stock-submit" onClick={submit} disabled={busy}
            className="ml-auto text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-700 hover:bg-emerald-800 px-4 py-1.5 transition-colors disabled:opacity-50">
            {busy ? "Creating…" : "Create Opening Stock"}
          </button>
          <button onClick={onClose}
            className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-300 hover:bg-slate-100 px-3 py-1.5 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectOpeningStockDialog;
